import { useMemo } from "react";
import { useLocalSearchParams } from "expo-router";
import { useQuery } from "@tanstack/react-query";
import { Image, Platform, StyleSheet, View } from "react-native";

import {
  AppText,
  BrandMark,
  Button,
  ButtonText,
  Card,
  ScrollScreen,
  Stack,
} from "~/components/ui";
import {
  radii,
  spacing,
  useThemedStyles,
  type ThemeColors,
} from "~/styles/theme";
import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import { workspaceLogoPublicUrl } from "~/utils/time-rounding";

type PublicInvoice = {
  id: string;
  invoice_number: string;
  status: string;
  issue_date: string;
  due_date: string | null;
  sent_at: string | null;
  viewed_at: string | null;
  paid_at: string | null;
  currency: string;
  subtotal_cents: number;
  gst_cents: number;
  rounding_adjustment_cents: number;
  total_cents: number;
  notes: string | null;
  payment_instructions: string | null;
  purchase_order_reference: string | null;
  client_reference: string | null;
  job_starts_on: string | null;
  job_ends_on: string | null;
  clients: {
    name: string;
    billing_address: string | null;
  } | null;
  workspaces: {
    name: string;
    legal_name: string | null;
    address: string | null;
    contact_email: string | null;
    phone: string | null;
    bank_account_name: string | null;
    bank_account_number: string | null;
    bank_name: string | null;
    brand_accent_hex: string | null;
    gst_number: string | null;
    logo_path: string | null;
    nzbn: string | null;
  };
};

type PublicInvoiceItem = {
  id: string;
  description: string;
  quantity: number;
  unit_amount_cents: number;
  gst_rate: number;
};

/** The public API returns the logo as a full URL; the screens build theirs from the stored path. */
function logoPathFrom(logoUrl: string | null | undefined) {
  return logoUrl?.split("/v1/logos/")[1] ?? null;
}

const DEFAULT_ACCENT = "#002c15";

function firstParam(value: string | string[] | undefined) {
  return Array.isArray(value) ? value[0] : value;
}

function brandAccent(value: string | null | undefined) {
  const trimmed = value?.trim() ?? "";
  if (!trimmed) return DEFAULT_ACCENT;
  const withHash = trimmed.startsWith("#") ? trimmed : `#${trimmed}`;
  if (!/^#[0-9A-Fa-f]{6}$/.test(withHash)) return DEFAULT_ACCENT;
  return withHash;
}

function formatMoney(cents: number, currency: string) {
  return new Intl.NumberFormat("en-NZ", {
    currency,
    style: "currency",
  }).format(cents / 100);
}

function lineSubtotal(item: PublicInvoiceItem) {
  return Math.round(Number(item.quantity) * item.unit_amount_cents);
}

function lineTotal(item: PublicInvoiceItem) {
  const subtotal = lineSubtotal(item);
  return subtotal + Math.round(subtotal * (item.gst_rate / 100));
}

function detailsLines(values: Array<string | null | undefined>) {
  return values.filter((value): value is string => Boolean(value?.trim()));
}

export default function PublicInvoiceScreen() {
  const stylesheet = useThemedStyles(makeStyles);
  const params = useLocalSearchParams<{ token?: string }>();
  const token = firstParam(params.token) ?? "";
  const query = useQuery({
    queryKey: ["public-invoice", token],
    queryFn: async () => {
      const data = await unwrap(api.getPublicInvoice({ path: { token } }));
      const { workspace, client, invoice, items } = data;
      return {
        invoice: {
          ...invoice,
          clients: client
            ? { name: client.name, billing_address: client.billing_address }
            : null,
          workspaces: {
            ...workspace,
            logo_path: logoPathFrom(workspace.logo_url),
          },
        } as unknown as PublicInvoice,
        items: items as unknown as PublicInvoiceItem[],
      };
    },
    enabled: Boolean(token),
  });
  const invoice = query.data?.invoice ?? null;
  const items = query.data?.items ?? [];
  const senderName = useMemo(
    () =>
      invoice?.workspaces.legal_name || invoice?.workspaces.name || "MintStint",
    [invoice],
  );
  const accent = brandAccent(invoice?.workspaces.brand_accent_hex);
  const logoUrl = workspaceLogoPublicUrl(invoice?.workspaces.logo_path);
  const bankLines = invoice
    ? detailsLines([
        invoice.workspaces.bank_name,
        invoice.workspaces.bank_account_name,
        invoice.workspaces.bank_account_number,
      ])
    : [];

  return (
    <ScrollScreen>
      <BrandMark />
      <Stack gap="md">
        <Card gap="sm">
          <AppText weight="semibold">Client invoice</AppText>
          <AppText size="xs" tone="muted">
            Review the invoice details, GST totals, and payment instructions
            from {senderName}.
          </AppText>
          {query.isLoading ? (
            <AppText size="xs" tone="muted">
              Loading invoice...
            </AppText>
          ) : null}
          {!token ? (
            <AppText size="xs" tone="danger">
              This invoice link is invalid.
            </AppText>
          ) : null}
          {query.error ? (
            <AppText size="xs" tone="danger">
              {query.error instanceof Error
                ? query.error.message
                : "Unable to load this invoice."}
            </AppText>
          ) : null}
          {invoice ? (
            <Button
              align="start"
              iconName="printer"
              variant="outline"
              onPress={() => {
                if (Platform.OS === "web" && typeof window !== "undefined") {
                  window.print();
                }
              }}
            >
              <ButtonText>Print copy</ButtonText>
            </Button>
          ) : null}
        </Card>

        {invoice ? (
          <>
            <View
              style={[stylesheet.brandBanner, { borderBottomColor: accent }]}
            >
              {logoUrl ? (
                <Image
                  accessibilityIgnoresInvertColors
                  resizeMode="contain"
                  source={{ uri: logoUrl }}
                  style={stylesheet.logo}
                />
              ) : null}
              <AppText size="lg" weight="bold" style={{ color: accent }}>
                {senderName}
              </AppText>
            </View>

            <View style={stylesheet.summaryGrid}>
              <Card gap="xs" style={stylesheet.summaryCard}>
                <AppText size="xs" tone="muted">
                  Invoice
                </AppText>
                <AppText size="lg" weight="bold" style={{ color: accent }}>
                  {invoice.invoice_number}
                </AppText>
              </Card>
              <Card gap="xs" style={stylesheet.summaryCard}>
                <AppText size="xs" tone="muted">
                  Status
                </AppText>
                <AppText size="lg" weight="bold">
                  {invoice.status}
                </AppText>
              </Card>
              <Card gap="xs" style={stylesheet.summaryCard}>
                <AppText size="xs" tone="muted">
                  Due
                </AppText>
                <AppText size="lg" weight="bold">
                  {invoice.due_date ?? "Not set"}
                </AppText>
              </Card>
              <Card gap="xs" style={stylesheet.summaryCard}>
                <AppText size="xs" tone="muted">
                  Total
                </AppText>
                <AppText size="lg" weight="bold" style={{ color: accent }}>
                  {formatMoney(invoice.total_cents, invoice.currency)}
                </AppText>
              </Card>
            </View>

            <View style={stylesheet.detailGrid}>
              <Card gap="sm" style={stylesheet.detailCard}>
                <AppText weight="semibold">From</AppText>
                <AppText>{senderName}</AppText>
                {detailsLines([
                  invoice.workspaces.address,
                  invoice.workspaces.contact_email,
                  invoice.workspaces.phone,
                ]).map((line) => (
                  <AppText key={line} size="xs">
                    {line}
                  </AppText>
                ))}
                <View style={stylesheet.metaRow}>
                  <AppText size="xs" tone="muted">
                    Issued
                  </AppText>
                  <AppText size="xs" tone="muted">
                    {invoice.issue_date}
                  </AppText>
                </View>
                <View style={stylesheet.metaRow}>
                  <AppText size="xs" tone="muted">
                    Viewed
                  </AppText>
                  <AppText size="xs" tone="muted">
                    {invoice.viewed_at ?? "Just now"}
                  </AppText>
                </View>
                {invoice.purchase_order_reference ? (
                  <View style={stylesheet.metaRow}>
                    <AppText size="xs" tone="muted">
                      PO
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {invoice.purchase_order_reference}
                    </AppText>
                  </View>
                ) : null}
                {invoice.client_reference ? (
                  <View style={stylesheet.metaRow}>
                    <AppText size="xs" tone="muted">
                      Client ref
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {invoice.client_reference}
                    </AppText>
                  </View>
                ) : null}
                {invoice.job_starts_on || invoice.job_ends_on ? (
                  <View style={stylesheet.metaRow}>
                    <AppText size="xs" tone="muted">
                      Job
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {[invoice.job_starts_on, invoice.job_ends_on]
                        .filter(Boolean)
                        .join(" – ")}
                    </AppText>
                  </View>
                ) : null}
              </Card>

              <Card gap="sm" style={stylesheet.detailCard}>
                <AppText weight="semibold">Bill to</AppText>
                <AppText>{invoice.clients?.name ?? "Client"}</AppText>
                {detailsLines([invoice.clients?.billing_address]).map(
                  (line) => (
                    <AppText key={line} size="xs">
                      {line}
                    </AppText>
                  ),
                )}
                {invoice.workspaces.gst_number ? (
                  <AppText size="xs" tone="muted">
                    GST number: {invoice.workspaces.gst_number}
                  </AppText>
                ) : null}
                {invoice.workspaces.nzbn ? (
                  <AppText size="xs" tone="muted">
                    NZBN: {invoice.workspaces.nzbn}
                  </AppText>
                ) : null}
              </Card>
            </View>

            <Card gap="sm">
              <AppText weight="semibold">Invoice lines</AppText>
              {items.length === 0 ? (
                <AppText size="xs" tone="muted">
                  No invoice items were attached.
                </AppText>
              ) : (
                <View>
                  {items.map((item, index) => (
                    <View
                      key={item.id}
                      style={[
                        stylesheet.itemRow,
                        index === 0 ? stylesheet.itemRowFirst : null,
                      ]}
                    >
                      <View style={stylesheet.itemCopy}>
                        <AppText weight="semibold">{item.description}</AppText>
                        <AppText size="xs" tone="muted">
                          {item.quantity} ×{" "}
                          {formatMoney(
                            item.unit_amount_cents,
                            invoice.currency,
                          )}{" "}
                          at {item.gst_rate}% GST
                        </AppText>
                      </View>
                      <AppText weight="semibold">
                        {formatMoney(lineTotal(item), invoice.currency)}
                      </AppText>
                    </View>
                  ))}
                </View>
              )}
            </Card>

            <View style={stylesheet.detailGrid}>
              <Card gap="sm" style={stylesheet.detailCard}>
                <AppText weight="semibold">Payment</AppText>
                {invoice.payment_instructions ? (
                  <AppText size="xs" tone="muted">
                    {invoice.payment_instructions}
                  </AppText>
                ) : (
                  <AppText size="xs" tone="muted">
                    Payment instructions will be confirmed directly by the
                    sender if needed.
                  </AppText>
                )}
                {bankLines.length ? (
                  <Stack gap="xs">
                    {bankLines.map((line) => (
                      <AppText key={line} size="xs">
                        {line}
                      </AppText>
                    ))}
                  </Stack>
                ) : null}
              </Card>

              <Card gap="sm" style={stylesheet.detailCard}>
                <AppText weight="semibold">Totals</AppText>
                <View style={stylesheet.metaRow}>
                  <AppText size="xs" tone="muted">
                    Subtotal
                  </AppText>
                  <AppText size="xs" tone="muted">
                    {formatMoney(invoice.subtotal_cents, invoice.currency)}
                  </AppText>
                </View>
                <View style={stylesheet.metaRow}>
                  <AppText size="xs" tone="muted">
                    GST
                  </AppText>
                  <AppText size="xs" tone="muted">
                    {formatMoney(invoice.gst_cents, invoice.currency)}
                  </AppText>
                </View>
                {invoice.rounding_adjustment_cents !== 0 ? (
                  <View style={stylesheet.metaRow}>
                    <AppText size="xs" tone="muted">
                      Rounding
                    </AppText>
                    <AppText size="xs" tone="muted">
                      {formatMoney(
                        invoice.rounding_adjustment_cents,
                        invoice.currency,
                      )}
                    </AppText>
                  </View>
                ) : null}
                <View
                  style={[
                    stylesheet.metaRow,
                    stylesheet.grandTotal,
                    { borderTopColor: accent },
                  ]}
                >
                  <AppText weight="semibold" style={{ color: accent }}>
                    Total
                  </AppText>
                  <AppText weight="semibold" style={{ color: accent }}>
                    {formatMoney(invoice.total_cents, invoice.currency)}
                  </AppText>
                </View>
              </Card>
            </View>

            {invoice.notes ? (
              <Card gap="sm">
                <AppText weight="semibold">Notes</AppText>
                <AppText size="xs" tone="muted">
                  {invoice.notes}
                </AppText>
              </Card>
            ) : null}
          </>
        ) : null}
      </Stack>
    </ScrollScreen>
  );
}

const makeStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    brandBanner: {
      borderBottomWidth: 3,
      gap: spacing.s,
      paddingBottom: spacing.m,
    },
    logo: {
      height: 48,
      maxWidth: 180,
      width: 180,
    },
    summaryGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    summaryCard: {
      flexGrow: 1,
      flexBasis: 150,
    },
    detailGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.m,
    },
    detailCard: {
      flexGrow: 1,
      flexBasis: 260,
    },
    metaRow: {
      alignItems: "center",
      flexDirection: "row",
      justifyContent: "space-between",
    },
    grandTotal: {
      borderTopWidth: 2,
      marginTop: spacing.s,
      paddingTop: spacing.s,
    },
    itemRow: {
      borderTopColor: colors.hairline,
      borderTopWidth: 1,
      flexDirection: "row",
      gap: spacing.m,
      justifyContent: "space-between",
      paddingVertical: spacing.m,
    },
    itemRowFirst: {
      borderTopWidth: 0,
    },
    itemCopy: {
      flex: 1,
      gap: spacing.xs,
    },
  });
